import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string | null;
  tenantId: string;
  roles: string[];
  permissions: Permissions;
  timezone: string;
  riskScore?: number;
  avatarUrl?: string;
  mustChangePassword?: boolean;
}

export type Permissions = Record<string, string[]>;

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;

  // Actions
  setAuth: (user: AuthUser, accessToken: string, refreshToken: string) => void;
  setUser: (user: AuthUser) => void;
  setAccessToken: (accessToken: string) => void;
  logout: () => void;
  updateUser: (partial: Partial<AuthUser>) => void;
}

type PersistedAuthState = Pick<
  AuthState,
  'user' | 'accessToken' | 'refreshToken' | 'isAuthenticated'
>;

export function extractUser(data: any): AuthUser | null {
  if (!data) return null;
  if (data.user && typeof data.user === 'object' && 'id' in data.user) {
    return data.user as AuthUser;
  }
  if ('id' in data) {
    return data as AuthUser;
  }
  return null;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,

      setAuth: (user, accessToken, refreshToken) =>
        set({ user: extractUser(user), accessToken, refreshToken, isAuthenticated: true }),

      setUser: (user) => set({ user: extractUser(user), isAuthenticated: true }),

      setAccessToken: (accessToken) => set({ accessToken }),

      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        }),

      updateUser: (partial) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...partial } : null,
        })),
    }),
    {
      name: 'bcrip-auth',
      version: 3,
      migrate: (persistedState) => {
        const state = persistedState as Partial<PersistedAuthState>;

        return {
          user: state.user ?? null,
          accessToken: state.accessToken ?? null,
          refreshToken: state.refreshToken ?? null,
          isAuthenticated: state.isAuthenticated === true,
        };
      },
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
